import { ResponsiveBar } from '@nivo/bar';
import { format } from 'd3';

import { graphColors } from 'colors';

import CustomTooltip from '../CustomTooltip';
import NoDataMessage from '../NoDataMessage';

type StackedDataItem = {
    id: string | number;
    barHeightValues: Record<string, number>;
    value?: Record<string, number>; // For tooltip display
};

export type StackedBarGraphProps = {
    bottomLegendLabel?: string;
    data: StackedDataItem[];
    leftLegendLabel: string;
    stackedBarLabels?: Record<string, string>;
    stackedBarColors?: Record<string, string>; // { "attempted": "#ff0000", "answered": "#00ff00" }
};

const StackedBarGraph = ({
    bottomLegendLabel,
    data,
    leftLegendLabel,
    stackedBarLabels = {},
    stackedBarColors = {},
}: StackedBarGraphProps) => {
    if (!data || data.length === 0) return <NoDataMessage />;

    const hasNonZeroData = data.some((datum) => Object.values(datum.barHeightValues).some((value) => value > 0));
    if (!hasNonZeroData) return <NoDataMessage />;

    // Get all unique keys from the barHeightValues
    const allKeys = Array.from(new Set(data.flatMap((datum) => Object.keys(datum.barHeightValues))));

    // Transform data for stacked bar chart
    const transformedData = data.map((datum) => ({
        id: datum.id,
        ...datum.barHeightValues,
    }));

    const getFormattedValue = (value: any) => {
        if (typeof value === 'number') {
            if (value.toString().length > 5) return format('~s')(value);
            return value.toLocaleString();
        }
        return value;
    };

    // Get color for a specific key, fallback to graphColors if not provided
    const getColorForKey = (key: string, index: number) =>
        stackedBarColors[key] || graphColors[index % graphColors.length];

    const renderTooltip = (datum: any) => {
        const values = datum.data;
        const timeLabel = `${datum.indexValue}`;

        // Find the original data item to get value
        const originalDataItem = data.find((item) => item.id === datum.indexValue);

        // Create tooltip content with all series values
        const tooltipContent = allKeys
            .map((key) => {
                const label = stackedBarLabels[key] || key;
                const value = originalDataItem?.value?.[key] ?? values[key] ?? 0;
                return `${label}: ${getFormattedValue(value)}`;
            })
            .join(', ');

        return <CustomTooltip color={datum.color} label={timeLabel} value={tooltipContent} />;
    };

    return (
        <div style={{ height: '400px', width: '100%' }}>
            <ResponsiveBar
                axisBottom={{
                    legend: bottomLegendLabel,
                    legendOffset: bottomLegendLabel ? 40 : 0,
                    legendPosition: 'middle',
                    tickSize: 0,
                }}
                axisLeft={{
                    tickSize: 0,
                    tickPadding: 5,
                    tickRotation: 0,
                    legend: leftLegendLabel,
                    legendPosition: 'middle',
                    legendOffset: -50,
                    truncateTickAt: 0,
                    format: (value) => getFormattedValue(value),
                }}
                borderWidth={2}
                borderColor={{
                    from: 'color',
                    modifiers: [['darker', 0.2]],
                }}
                colors={allKeys.map((key, index) => getColorForKey(key, index))}
                data={transformedData}
                enableGridY={false}
                keys={allKeys}
                label={''}
                margin={{
                    top: 16,
                    right: 16,
                    bottom: bottomLegendLabel ? 60 : 32,
                    left: 70,
                }}
                tooltip={(datum) => renderTooltip(datum)}
            />
        </div>
    );
};

export default StackedBarGraph;
